import { IWedding_Website_Section } from '@bridebook/models/source/models/Weddings.types';
import { NON_REORDERABLE_SECTIONS } from '@bridebook/web/lib/wedding-website/constants';
import { TSectionName } from '@bridebook/web/lib/wedding-website/types';
import { ReactNode } from 'react';

/**
 * Shared section-ordering helpers used by the view-mode layouts (Primary, Motif).
 * Pure / framework-agnostic: they take section data + a section→component registry and produce an ordered list of
 * entries. Each consumer decides how to render the entries (with or without animations/dividers).
 */

export type SectionKind = 'photo' | 'content';

export interface SectionEntry {
  key: string;
  kind: SectionKind;
  visible: boolean;
  element: ReactNode;
}

/**
 * Merge content + photos according to the rules:
 * - Use all photos.
 * - Place photos:
 *   - before first section (P1),
 *   - after 2nd and 4th section when possible (P2/P3),
 *   - otherwise between/after sections as evenly / often as possible.
 *
 * Special cases for small numbers of sections:
 * - 0 sections: only photos.
 * - 1 section: P1 - S1 - remaining photos.
 * - 2 sections: P1 - S1 - P2 - S2 - P3 (if 3 photos).
 */
export const arrangeSections = (
  content: SectionEntry[],
  photos: SectionEntry[],
): SectionEntry[] => {
  const contentCount = content.length;
  const photoCount = photos.length;

  if (contentCount === 0) {
    return photos;
  }

  if (photoCount === 0) {
    return content;
  }

  // 1 section: P1 - S1 - P2 - P3...
  if (contentCount === 1) {
    const gaps: SectionEntry[][] = [[], []]; // [before S1, after S1]

    photos.forEach((photo, index) => {
      if (index === 0) {
        gaps[0].push(photo);
      } else {
        gaps[1].push(photo);
      }
    });

    return [...gaps[0], content[0], ...gaps[1]];
  }

  // 2 sections: P1 - S1 - P2 - S2 - P3...
  if (contentCount === 2) {
    const gaps: SectionEntry[][] = [[], [], []]; // [before S1, between S1/S2, after S2]

    photos.forEach((photo, index) => {
      const gapIndex = index <= 2 ? index : 2; // any extra photos go after S2
      gaps[gapIndex].push(photo);
    });

    return [...gaps[0], content[0], ...gaps[1], content[1], ...gaps[2]];
  }

  // 3+ sections:
  // Preferred positions for up to 3 photos:
  //   P1 -> before S1      (gap 0)
  //   P2 -> after S2       (gap 2)
  //   P3 -> after S4       (gap 4), if it exists
  const contentItemCount = contentCount;
  const gaps: SectionEntry[][] = Array.from({ length: contentItemCount + 1 }, () => []); // 0..m

  const preferredSlots = [0, 2, 4].filter((slot) => slot <= contentItemCount);

  photos.forEach((photo, index) => {
    if (index < preferredSlots.length) {
      const slot = preferredSlots[index];
      gaps[slot].push(photo);
    } else {
      // Any extra photos go after the last section
      gaps[contentItemCount].push(photo);
    }
  });

  const result: SectionEntry[] = [];
  for (let i = 0; i < contentItemCount; i++) {
    result.push(...gaps[i]);
    result.push(content[i]);
  }
  result.push(...gaps[contentItemCount]);

  return result;
};

interface BuildContentSectionsOptions {
  /**
   * Optional element rendered first and always visible (covers the non-reorderable overview/hero).
   * When omitted, only the reorderable sections are returned — useful for layouts (e.g. Motif) that render their
   * hero separately and therefore want the photo interleaving to start after it rather than before it.
   */
  overview?: ReactNode;
  /** Maps a section name to the element that renders it. */
  registry: Record<string, ReactNode>;
}

/**
 * Builds content section entries from website.sections in the correct order.
 * - The overview (when provided) is always first (covers non-reorderable sections).
 * - Reorderable sections follow in the order defined by website.sections.
 * - Sections without a registry entry (e.g. future sections) are skipped.
 */
export const buildContentSections = (
  sections: IWedding_Website_Section[],
  { overview, registry }: BuildContentSectionsOptions,
): SectionEntry[] => {
  const reorderableSections = sections
    .filter((section) => {
      // Skip non-reorderable sections (handled by the overview)
      const sectionName = section.name as TSectionName;
      return !NON_REORDERABLE_SECTIONS.includes(sectionName);
    })
    .map((section): SectionEntry | null => {
      const component = registry[section.name];
      // Skip sections without a component mapping (e.g., future sections)
      if (!component) return null;

      return {
        key: section.name,
        kind: 'content',
        visible: !!section.visible,
        element: component,
      };
    })
    .filter((entry): entry is SectionEntry => entry !== null);

  if (overview === undefined) {
    return reorderableSections;
  }

  // Overview is always first and always visible
  const overviewSection: SectionEntry = {
    key: 'overview',
    kind: 'content',
    visible: true,
    element: overview,
  };

  return [overviewSection, ...reorderableSections];
};
